import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ComponentElementException } from '@inter-ikea-kompis/exceptions';
import type { IKeyboard } from '@inter-ikea-kompis/keyboard-manager';
import {
	InputModeEnum,
	KeyboardManager,
	KeyboardTypeEnum
} from '@inter-ikea-kompis/keyboard-manager';
import type { ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import KeyboardLayout from '../../enums/KeyboardLayout.js';
import Keyboard from '../keyboard/Keyboard.js';
import KeyboardInjectorStyle from './KeyboardInjectorStyle.js';

const PADDING = 64;

/**
 * A virtual keyboard. This component is used with this.__keyboardManager.
 *
 * @slot Your app.
 */
export default class KeyboardInjector extends KompisElement implements IKeyboard {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Done button label.
	@property({ type: String, required: true })
	public doneButtonLabel!: string;

	// The keyboard layout.
	@property({ type: String })
	public layout: string = KeyboardLayout.qwerty.name;

	// Locale is used to determine the type of decimal symbol (comma or point) on the "decimal" keyboard layout.
	@property({ type: String })
	public locale: string | null = null;

	// Override z-index of the Keyboard.
	@property({ type: Number })
	public zIndex = 501;

	private listeners = {
		keyPressed: this.onKeyPressed.bind(this),
		done: this.onDoneClick.bind(this)
	};

	private type: KeyboardTypeEnum | null = KeyboardTypeEnum.text;
	private input: HTMLInputElement | null = null;
	private offsetY = 0;
	private keyboardManager = new KeyboardManager();

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		return html`
			<style>
				${KeyboardInjectorStyle.getStyle({ offsetY: this.offsetY })}
			</style>
			<div class="app">
				<slot></slot>
			</div>
			<kompis-keyboard
				@keyPressed="${this.listeners.keyPressed}"
				@done="${this.listeners.done}"
				.zIndex="${this.zIndex}"
				.theme="${this.theme}"
				.layout="${this.layout}"
				.doneButtonLabel="${this.doneButtonLabel}"
				.type="${this.type}"
				.visible="${Boolean(this.input)}"
				.locale="${this.locale}"
			></kompis-keyboard>
		`;
	}

	public override connectedCallback(): void {
		this.customElements.define('kompis-keyboard', Keyboard);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(window as any).$ikeaKompisKeyboardInjectorTagNames =
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(window as any).$ikeaKompisKeyboardInjectorTagNames || [];
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(window as any).$ikeaKompisKeyboardInjectorTagNames.push(this.tagName);
		super.connectedCallback();
		this.keyboardManager.addKeyboard(this);
	}

	/**
	 * Call this function when the input is focused.
	 *
	 * @param inputElement Input Element.
	 */
	public setFocus(inputElement: HTMLInputElement): void {
		const inputMode = inputElement.getAttribute('inputmode');

		if (!inputMode) {
			throw new ComponentElementException(
				this,
				'The attribute "inputmode" is required on input elements.'
			);
		}

		if (inputElement.type !== 'text') {
			throw new ComponentElementException(
				this,
				'The Keyboard only supports type "text" on input elements.'
			);
		}

		this.type = this.getKeyboardType(inputMode);

		if (this.type === null) {
			throw new ComponentElementException(
				this,
				`The InputModeEnum "${inputMode}" set as an attribute on the input element is not supported by the Keyboard.`
			);
		}

		this.input = inputElement;
		this.offsetY = this.getOffsetY();
		this.requestUpdate();
	}

	/**
	 * Called when the input lost focus.
	 *
	 */
	public removeFocus(): void {
		this.input = null;
		this.offsetY = 0;
		this.requestUpdate();
	}

	public override disconnectedCallback(): void {
		super.disconnectedCallback();
		this.keyboardManager.removeKeyboard(this);
	}

	/**
	 * Handles key pressed.
	 *
	 * @param event Event.
	 */
	private onKeyPressed(event: CustomEvent): void {
		if (event.detail.keyCode === 'backspace') {
			this.removeText();
		} else {
			this.insertCharacter(event.detail.keyCode);
		}
		this.input?.dispatchEvent(new CustomEvent('input', { bubbles: true, cancelable: true }));
	}

	/**
	 * On done click.
	 */
	private onDoneClick(): void {
		if (this.input) {
			this.input.blur();
			this.removeFocus();
		}
	}

	/**
	 * Inserts a character in the input field.
	 *
	 * @param keyCode Key code.
	 */
	private insertCharacter(keyCode: string): void {
		if (!this.input) {
			return;
		}

		const selectionStart = this.input.selectionStart;
		const selectionEnd = this.input.selectionEnd;
		const length = this.input.value.length;

		if (selectionStart === length) {
			// If the cursor is at the end of the string
			this.input.value += keyCode;
		} else if (selectionStart !== null && selectionEnd !== null) {
			const firstHalf = this.input.value.slice(0, selectionStart);
			const secondHalf = this.input.value.slice(selectionEnd);

			this.input.value = firstHalf + keyCode + secondHalf;

			const newSelectionStart = selectionStart + 1;
			this.input.setSelectionRange(newSelectionStart, newSelectionStart);
		}
	}

	/**
	 * Removes text.
	 */
	private removeText(): void {
		if (!this.input) {
			return;
		}

		const selectionStart = this.input.selectionStart;
		const selectionEnd = this.input.selectionEnd;
		const length = this.input.value.length;

		if (selectionStart === length) {
			// If the cursor is at the end of the string
			this.input.value = this.input.value.slice(0, length - 1); // Remove the last character.
		} else if (selectionStart !== null && selectionStart === selectionEnd && selectionStart !== 0) {
			// If the cursor is not at the end of a string and no text is selected
			const firstHalf = this.input.value.slice(0, selectionStart - 1);
			const secondHalf = this.input.value.slice(selectionEnd);

			this.input.value = firstHalf + secondHalf;

			this.input.setSelectionRange(selectionStart - 1, selectionStart - 1);
		} else if (selectionStart !== null && selectionEnd !== null) {
			// If text is selected
			const firstHalf = this.input.value.slice(0, selectionStart);
			const secondHalf = this.input.value.slice(selectionEnd);

			this.input.value = firstHalf + secondHalf;

			this.input.setSelectionRange(selectionStart, selectionStart);
		}
	}

	/**
	 * Returns keyboard type.
	 *
	 * @param inputMode Input mode.
	 * @returns The keyboard type.
	 */
	private getKeyboardType(inputMode: string): KeyboardTypeEnum | null {
		switch (inputMode) {
			case InputModeEnum.decimal:
				return KeyboardTypeEnum.decimal;
			case InputModeEnum.numeric:
				return KeyboardTypeEnum.number;
			case InputModeEnum.tel:
				return KeyboardTypeEnum.tel;
			case InputModeEnum.email:
				return KeyboardTypeEnum.email;
			case InputModeEnum.text:
			case InputModeEnum.search:
			case InputModeEnum.url:
				return KeyboardTypeEnum.text;
			case InputModeEnum.measurement:
				return KeyboardTypeEnum.measurement;
			default:
				return null;
		}
	}

	/**
	 * @returns GetOffset Y.
	 */
	private getOffsetY(): number {
		let inputBottom = this.getInputBottom();
		let appTop = this.getAppTop();
		if (inputBottom === null || appTop === null) {
			return 0;
		}

		const distanceOutsideViewport = Math.max(inputBottom - appTop - window.innerHeight, 0);

		if (distanceOutsideViewport > 0) {
			this.input?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
		}

		inputBottom = this.getInputBottom();
		appTop = this.getAppTop();
		if (inputBottom === null || appTop === null) {
			return 0;
		}

		const diff =
			inputBottom +
			this.theme.spacing.space200 -
			this.getVisibleKeyboardTop() -
			distanceOutsideViewport -
			appTop;

		return Math.max(0, diff + PADDING);
	}

	/**
	 * Returns the bottom position of the input in px.
	 *
	 * @returns Bottom position of the input in px.
	 */
	private getInputBottom(): number | null {
		const inputRect = this.input?.getBoundingClientRect();
		return inputRect?.bottom ?? null;
	}

	/**
	 * Returns the top position of the app container in px.
	 *
	 * @returns App top position in px.
	 */
	private getAppTop(): number | null {
		return this.shadowRoot?.querySelector('.app')?.getBoundingClientRect().top ?? null;
	}

	/**
	 * Returns the top position of the keyboard when it's visible after the translate transition in px.
	 *
	 * @returns The top position of the keyboard when it's visible after the translate transition.
	 */
	private getVisibleKeyboardTop(): number {
		return window.innerHeight - this.getKeyboardHeight();
	}

	/**
	 * Returns the keyboard view height in px.
	 *
	 * @returns Keyboard height in px.
	 */
	private getKeyboardHeight(): number {
		const keyboard = this.shadowRoot?.querySelector(
			this.customElements.getInternalName('kompis-keyboard')
		);
		return (<HTMLElement>keyboard?.shadowRoot?.querySelector('.keyboard'))?.offsetHeight ?? 0;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-keyboard-injector': KeyboardInjector;
	}
}
